import type { ContactMatchStatus, TaxonomyMode } from "./submissions.service.js";
import * as api from "./client.js";

/** The statuses the backfill may (re)match. `manual` is never touched server-side. */
export const BACKFILL_STATUSES = ["pending", "unmatched", "ambiguous"] as const;
export type BackfillStatus = (typeof BACKFILL_STATUSES)[number];

export type ContactMatchBackfillRequest = {
  publicationId?: number;
  editionId?: string;
  edition?: string;
  statuses?: BackfillStatus[];
  dryRun?: boolean;
  /** 1–500, server default 50. */
  limit?: number;
  cursor?: string;
  taxonomy?: TaxonomyMode;
};

export type ContactMatchBackfillResponse = {
  /** Entries in this batch: counted (dry run) or matched (real run). */
  processed: number;
  /** Real runs only: the status each entry ended up in. */
  results?: Array<{ entryId: string; status: ContactMatchStatus }>;
  /** Pass back as `cursor` for the next batch; null when this was the last one. */
  nextCursor: string | null;
  dryRun: boolean;
  requestId?: string;
};

export type ContactMatchStatsResponse = {
  byStatus: Record<string, number>;
  byPublicationEdition: Array<{
    publicationId: number | null;
    publicationName: string;
    edition: string | null;
    editionId: string | null;
    byStatus: Record<string, number>;
  }>;
  requestId?: string;
};

/**
 * submission-api's contact-match backfill and stats (DEV-2662), via staff-bff's
 * `/v1/submission/*` proxy. Both need staff.submission.write / .read. A real run (not a
 * dry run) answers 409 `contact_match_disabled` while the submission-entry-contact-match
 * flag is off.
 */
export const contactMatchBackfill = {
  /** `signal` aborts the in-flight batch (the page passes one so leaving it stops the run). */
  run: (
    body: ContactMatchBackfillRequest,
    signal?: AbortSignal,
  ): Promise<ContactMatchBackfillResponse> =>
    signal
      ? api.postWithSignal<ContactMatchBackfillResponse>(
          "/v1/submission/submissions/contact-match/backfill",
          body,
          signal,
        )
      : api.post<ContactMatchBackfillResponse>(
          "/v1/submission/submissions/contact-match/backfill",
          body,
        ),

  stats: (taxonomy?: TaxonomyMode): Promise<ContactMatchStatsResponse> =>
    api.get<ContactMatchStatsResponse>(
      `/v1/submission/submissions/contact-match/stats${taxonomy ? `?taxonomy=${taxonomy}` : ""}`,
    ),
};
